import { useEffect, useMemo, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  dueState,
  localDateKey,
  MAX_LIST_DESCRIPTION_LENGTH,
  MAX_LIST_ITEM_LENGTH,
  type List,
  type ListItem,
  type PublicUser,
} from '@avslack/shared';
import {
  useCreateListItem,
  useDeleteListItem,
  useUpdateList,
  useUpdateListItem,
} from '../data/lists';
import { useUsers } from '../data/users';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { useConfirm } from './Confirm';

/**
 * Egy lista szerkesztoje: pipalhato tetelek felelossel es hataridovel.
 *
 * ─── Miert nem canvas? ──────────────────────────────────────────────────────
 *
 * Egy canvasba is lehet listat irni, de az SZOVEG marad. Itt a harom oszlop
 * (mi, ki, mikor) kulon mezo, ezert ranezesre latszik, mi van kesz, mi jar le
 * ma, es mi az, aminek nincs gazdaja.
 *
 * ─── Mentes ─────────────────────────────────────────────────────────────────
 *
 * Nincs mentes gomb. A pipalas, a felelos es a hatarido AZONNAL megy; a
 * szoveges mezok a fokusz elvesztesekor (`blur`), mert gepeles kozben minden
 * leutest elkuldeni felesleges forgalom lenne.
 */
export function ListEditor({
  list,
  channelId,
  canEdit,
}: {
  list: List;
  channelId: string;
  canEdit: boolean;
}) {
  const updateList = useUpdateList(channelId);
  const createItem = useCreateListItem(channelId);
  const updateItem = useUpdateListItem(channelId);
  const deleteItem = useDeleteListItem(channelId);
  const confirm = useConfirm();

  /** A cim es a leiras helyi masolata - gepeles kozben nem kerdezzuk a szervert. */
  const [title, setTitle] = useState(list.title);
  const [description, setDescription] = useState(list.description);

  useEffect(() => {
    setTitle(list.title);
    setDescription(list.description);
  }, [list.id, list.title, list.description]);

  /** Mit mutatunk: mindent, vagy csak a hatralevoket. */
  const [showDone, setShowDone] = useState(true);

  const items = useMemo(
    () => (showDone ? list.items : list.items.filter((item) => !item.isDone)),
    [list.items, showDone],
  );

  const torol = async (item: ListItem): Promise<void> => {
    const ok = await confirm({
      title: 'Tétel törlése',
      body: item.text ? <>„{item.text}” eltűnik a listából.</> : 'A tétel eltűnik a listából.',
    });
    if (ok) deleteItem.mutate(item.id);
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
      <div className="mx-auto max-w-3xl">
        {/* ─── Fejlec ───────────────────────────────────────────────────── */}
        <input
          aria-label="A lista címe"
          value={title}
          disabled={!canEdit}
          maxLength={120}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => {
            const trimmed = title.trim();
            if (!trimmed || trimmed === list.title) return setTitle(list.title);
            updateList.mutate({ listId: list.id, patch: { title: trimmed } });
          }}
          className="-mx-1.5 w-[calc(100%+0.75rem)] rounded-control bg-transparent px-1.5 font-display text-2xl font-semibold tracking-tight outline-none transition-colors duration-(--duration-quick) placeholder:text-ink-faint focus:bg-surface-hover"
          placeholder="A lista címe"
        />

        <input
          aria-label="A lista leírása"
          value={description}
          disabled={!canEdit}
          maxLength={MAX_LIST_DESCRIPTION_LENGTH}
          onChange={(event) => setDescription(event.target.value)}
          onBlur={() => {
            if (description === list.description) return;
            updateList.mutate({ listId: list.id, patch: { description } });
          }}
          className="-mx-1.5 mt-1 w-[calc(100%+0.75rem)] rounded-control bg-transparent px-1.5 text-sm text-ink-muted outline-none transition-colors duration-(--duration-quick) placeholder:text-ink-faint focus:bg-surface-hover"
          placeholder="Mire való ez a lista?"
        />

        {/* ─── Szuro es allas ───────────────────────────────────────────── */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowDone((value) => !value)}
            className="flex items-center gap-1.5 rounded-control border border-border-soft px-2.5 py-1 text-xs text-ink-muted transition-colors duration-(--duration-quick) hover:border-border-strong hover:text-ink"
          >
            <Icon name={showDone ? 'mutat' : 'rejt'} size="sm" />
            {showDone ? 'Minden tétel' : 'Csak a hátralévők'}
          </button>

          <span className="text-xs text-ink-faint">
            {list.doneCount}/{list.totalCount} kész
          </span>
        </div>

        {/* ─── A tablazat ───────────────────────────────────────────────── */}
        <div className="mt-3 overflow-hidden rounded-card border border-border-soft">
          {/*
            MOBILON csak a "Tétel" felirat all itt.

            A felelos es a hatarido ott nem kulon oszlop, hanem a tetel ALATT
            egy masodik sorban - 390 keppontos kepernyon harom oszlop egymas
            mellett annyit hagyna a szovegnek, hogy egy szo sem ferne ki.
          */}
          <div className="grid grid-cols-[2rem_1fr_2rem] items-center gap-2 border-b border-border-soft bg-surface-sunken px-3 py-2 text-2xs font-semibold uppercase tracking-wide text-ink-faint sm:grid-cols-[2rem_1fr_9rem_8rem_2rem]">
            <span aria-hidden="true" />
            <span>Tétel</span>
            <span className="hidden sm:inline">Felelős</span>
            <span className="hidden sm:inline">Határidő</span>
            <span aria-hidden="true" />
          </div>

          {items.length === 0 && (
            <p className="px-3 py-4 text-sm text-ink-faint">
              {list.totalCount === 0 ? 'Még nincs tétel.' : 'Minden tétel kész.'}
            </p>
          )}

          {items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              canEdit={canEdit}
              onPatch={(patch) => updateItem.mutate({ itemId: item.id, patch })}
              onDelete={() => void torol(item)}
            />
          ))}

          {canEdit && (
            <NewItemRow
              onCreate={(text) => createItem.mutate({ listId: list.id, input: { text } })}
              pending={createItem.isPending}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ItemRow({
  item,
  canEdit,
  onPatch,
  onDelete,
}: {
  item: ListItem;
  canEdit: boolean;
  onPatch: (patch: {
    text?: string;
    isDone?: boolean;
    assigneeId?: string | null;
    dueDate?: string | null;
  }) => void;
  onDelete: () => void;
}) {
  const [text, setText] = useState(item.text);
  useEffect(() => setText(item.text), [item.id, item.text]);

  return (
    <div className="group grid grid-cols-[2rem_1fr_2rem] items-center gap-x-2 gap-y-0.5 border-b border-border-soft px-3 py-1.5 last:border-b-0 hover:bg-surface-hover sm:grid-cols-[2rem_1fr_9rem_8rem_2rem]">
      {/*
        A PIPA egy gomb, nem `checkbox`.

        Igy tudja a sajat allapotat kimondani ("Kész" / "Mégsem kész"), es nem
        kell kulon cimke-elem hozza.
      */}
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => onPatch({ isDone: !item.isDone })}
        title={item.isDone ? 'Mégsem kész' : 'Kész'}
        aria-pressed={item.isDone}
        className={`grid size-5 place-items-center rounded-full border text-2xs transition-colors duration-(--duration-quick) ${
          item.isDone
            ? 'border-presence-active bg-presence-active text-white'
            : 'border-border-strong text-transparent hover:border-accent'
        }`}
      >
        <Icon name="pipa" size="sm" />
      </button>

      <input
        aria-label="Teendő"
        value={text}
        disabled={!canEdit}
        maxLength={MAX_LIST_ITEM_LENGTH}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          if (text === item.text) return;
          onPatch({ text });
        }}
        placeholder="Mi a teendő?"
        className={`min-w-0 rounded-control bg-transparent px-1 text-sm outline-none transition-colors duration-(--duration-quick) placeholder:text-ink-faint focus:bg-surface-hover ${
          item.isDone ? 'text-ink-faint line-through' : ''
        }`}
      />

      {/*
        A FELELOS es a HATARIDO mobilon a tetel ala kerul, egy sorba.

        A `sm:contents` asztalon feloldja ezt a burkolot, tehat ott ket
        kulon racs-oszlop marad - ugyanaz a tablazat, mint eddig.
      */}
      <div className="col-start-2 col-end-4 row-start-2 flex items-center gap-3 pb-1 sm:contents sm:pb-0">
        <AssigneePicker
          assignee={item.assignee}
          canEdit={canEdit}
          onPick={(userId) => onPatch({ assigneeId: userId })}
        />

        <DuePicker
          dueDate={item.dueDate}
          isDone={item.isDone}
          canEdit={canEdit}
          onPick={(date) => onPatch({ dueDate: date })}
        />
      </div>

      {canEdit ? (
        <button
          type="button"
          onClick={onDelete}
          title="Tétel törlése"
          aria-label="Tétel törlése"
          className="col-start-3 row-start-1 grid size-6 place-items-center rounded-control text-ink-faint opacity-0 transition-opacity duration-(--duration-quick) hover:text-danger focus-visible:opacity-100 group-hover:opacity-100 sm:col-start-auto sm:row-start-auto [@media(hover:none)]:opacity-100"
        >
          <Icon name="torles" size="sm" />
        </button>
      ) : (
        <span
          aria-hidden="true"
          className="col-start-3 row-start-1 sm:col-start-auto sm:row-start-auto"
        />
      )}
    </div>
  );
}

/** Uj tetel: egyetlen mezo, Enterre menti es rogton nyit egy ujat. */
function NewItemRow({ onCreate, pending }: { onCreate: (text: string) => void; pending: boolean }) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setText('');
    // A kurzor marad: egy lista irasa sorozat, nem egyetlen bejegyzes.
    inputRef.current?.focus();
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <span aria-hidden="true" className="grid size-5 place-items-center text-ink-faint">
        <Icon name="uj" size="sm" />
      </span>
      <input
        aria-label="Új tétel"
        ref={inputRef}
        value={text}
        disabled={pending}
        maxLength={MAX_LIST_ITEM_LENGTH}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') submit();
          if (event.key === 'Escape') setText('');
        }}
        onBlur={submit}
        placeholder="Új tétel — Enter a hozzáadáshoz"
        className="min-w-0 flex-1 rounded-control bg-transparent px-1 text-sm outline-none transition-colors duration-(--duration-quick) placeholder:text-ink-faint focus:bg-surface-hover"
      />
    </div>
  );
}

function AssigneePicker({
  assignee,
  canEdit,
  onPick,
}: {
  assignee: PublicUser | null;
  canEdit: boolean;
  onPick: (userId: string | null) => void;
}) {
  const { users, nameOf } = useUsers();
  const [query, setQuery] = useState('');

  const talalatok = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const lista = users.filter((user) => !user.isBot && user.isActive);
    return (
      needle ? lista.filter((user) => nameOf(user).toLowerCase().includes(needle)) : lista
    ).slice(0, 30);
  }, [users, query, nameOf]);

  if (!canEdit) {
    return assignee ? (
      <span className="flex min-w-0 items-center gap-1.5 text-xs">
        <Avatar
          userId={assignee.id}
          name={nameOf(assignee)}
          avatarUrl={assignee.avatarUrl}
          size="sm"
        />
        <span className="truncate">{nameOf(assignee)}</span>
      </span>
    ) : (
      <span className="text-xs text-ink-faint">—</span>
    );
  }

  return (
    <DropdownMenu.Root onOpenChange={(open) => !open && setQuery('')}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={assignee ? `Felelős: ${nameOf(assignee)}` : 'Felelős hozzárendelése'}
          className="flex min-w-0 items-center gap-1.5 rounded-control px-1 py-0.5 text-xs text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
        >
          {assignee ? (
            <>
              <Avatar
                userId={assignee.id}
                name={nameOf(assignee)}
                avatarUrl={assignee.avatarUrl}
                size="sm"
              />
              <span className="truncate">{nameOf(assignee)}</span>
            </>
          ) : (
            <>
              <Icon name="meghivas" size="sm" />
              <span className="text-ink-faint">Nincs</span>
            </>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="animate-fade-rise z-50 max-h-72 w-60 overflow-y-auto rounded-card border border-border-soft bg-surface-raised p-1 shadow-(--shadow-modal)"
        >
          <div className="p-1">
            <input
              aria-label="Felelős keresése"
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder="Név…"
              className="w-full rounded-control border border-border-soft bg-surface px-2 py-1 text-xs outline-none focus:border-accent"
            />
          </div>

          {assignee && (
            <DropdownMenu.Item
              onSelect={() => onPick(null)}
              className="flex cursor-pointer items-center gap-2 rounded-control px-2 py-1.5 text-xs text-ink-muted outline-none data-[highlighted]:bg-surface-hover"
            >
              <Icon name="bezaras" size="sm" />
              Felelős törlése
            </DropdownMenu.Item>
          )}

          {talalatok.map((user) => (
            <DropdownMenu.Item
              key={user.id}
              onSelect={() => onPick(user.id)}
              className="flex cursor-pointer items-center gap-2 rounded-control px-2 py-1.5 text-xs outline-none data-[highlighted]:bg-surface-hover"
            >
              <Avatar userId={user.id} name={nameOf(user)} avatarUrl={user.avatarUrl} size="sm" />
              <span className="truncate">{nameOf(user)}</span>
            </DropdownMenu.Item>
          ))}

          {talalatok.length === 0 && (
            <p className="px-2 py-2 text-xs text-ink-faint">Nincs találat.</p>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Hatarido.
 *
 * A LEJART es a MAI kiemelve latszik - ez a lista egyetlen szinjelzese, es
 * pont azt mondja el, amiert az ember ranez. A kesz teteleknel nincs kiemeles:
 * ami elkeszult, arrol mindegy, hogy tegnapra szolt.
 */
function DuePicker({
  dueDate,
  isDone,
  canEdit,
  onPick,
}: {
  dueDate: string | null;
  isDone: boolean;
  canEdit: boolean;
  onPick: (date: string | null) => void;
}) {
  const allapot = isDone ? 'nincs' : dueState(dueDate);

  const szin =
    allapot === 'lejart' ? 'text-danger' : allapot === 'ma' ? 'text-alert' : 'text-ink-muted';

  const felirat = dueDate ? formatDue(dueDate) : 'Nincs';

  if (!canEdit) return <span className={`text-xs ${szin}`}>{felirat}</span>;

  return (
    <span className="relative flex items-center">
      <input
        aria-label="Határidő"
        type="date"
        value={dueDate ?? ''}
        onChange={(event) => onPick(event.target.value || null)}
        title={dueDate ? `Határidő: ${felirat}` : 'Határidő megadása'}
        className={`w-full rounded-control bg-transparent px-1 py-0.5 text-xs outline-none transition-colors duration-(--duration-quick) hover:bg-surface-hover focus:bg-surface-hover ${szin} ${
          dueDate ? '' : 'text-ink-faint'
        }`}
      />
    </span>
  );
}

/** Rovid, olvashato datum: "ma", "holnap", egyebkent "szept. 12." */
function formatDue(date: string): string {
  const ma = localDateKey(new Date());
  if (date === ma) return 'ma';

  const holnap = new Date();
  holnap.setDate(holnap.getDate() + 1);
  if (date === localDateKey(holnap)) return 'holnap';

  const [ev, ho, nap] = date.split('-').map(Number);
  return new Date(ev!, ho! - 1, nap!).toLocaleDateString('hu-HU', {
    month: 'short',
    day: 'numeric',
  });
}
